import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mic, MicOff, Send, PhoneOff, Volume2, VolumeX, Loader2, Keyboard, FileText } from "lucide-react";
import { useVoice } from "@/hooks/useVoice";
import { PERSONA_AVATARS, STAGE_PERSONAS } from "@/lib/persona-avatars";
import { toast } from "sonner";

export default function ConversationModePage() {
  const { sessionId: urlSessionId } = useParams();
  const navigate = useNavigate();

  const [sessionId, setSessionId] = useState(urlSessionId || null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [learningStage, setLearningStage] = useState(null);
  const [phase, setPhase] = useState(urlSessionId ? "loading" : "idle");
  const [voiceActive, setVoiceActive] = useState(false); // Is the voice conversation mode ON?
  const [isListening, setIsListening] = useState(false);
  const [micPaused, setMicPaused] = useState(false); // User manually muted mic (KI still speaks)
  const [liveTranscript, setLiveTranscript] = useState("");
  const [sending, setSending] = useState(false);
  const [ending, setEnding] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [aiStatus, setAiStatus] = useState("idle"); // idle, speaking, thinking
  const [sessionShouldEnd, setSessionShouldEnd] = useState(false);
  const [turnInfo, setTurnInfo] = useState({ turn: 0, max_turns: 50 });
  const [akteInfo, setAkteInfo] = useState(null);

  // Derive persona from learning stage (Bug 2 fix)
  const stagePersona = learningStage ? STAGE_PERSONAS[learningStage] : null;
  const personaId = stagePersona?.id || "dr_mueller";
  const avatarInfo = stagePersona || PERSONA_AVATARS.dr_mueller;

  const { speak, stop: stopVoice, isSpeaking, isLoading: voiceLoading } = useVoice(personaId, learningStage);
  const recognitionRef = useRef(null);
  const sendTimeoutRef = useRef(null);
  const messagesEndRef = useRef(null);
  const lastSpokenIdxRef = useRef(-1);
  const speakRef = useRef(speak);
  const micPausedRef = useRef(false);
  const sendingRef = useRef(false);
  const speakingRef = useRef(false);
  const voiceActiveRef = useRef(false);

  // BUG 1 FIX: Reset ALL state when urlSessionId changes (stage switch)
  useEffect(() => {
    setSessionId(urlSessionId || null);
    setMessages([]);
    setInputText("");
    setLearningStage(null);
    setPhase(urlSessionId ? "loading" : "idle");
    setVoiceActive(false);
    setIsListening(false);
    setMicPaused(false);
    setLiveTranscript("");
    setSending(false);
    setEnding(false);
    setSessionShouldEnd(false);
    setTurnInfo({ turn: 0, max_turns: 50 });
    setAkteInfo(null);
    setAiStatus("idle");
    lastSpokenIdxRef.current = -1;
    micPausedRef.current = false;
    sendingRef.current = false;
    speakingRef.current = false;
    voiceActiveRef.current = false;
  }, [urlSessionId]);

  // Keep ALL refs in sync — refs are readable inside any closure, always current
  useEffect(() => { speakRef.current = speak; }, [speak]);
  useEffect(() => { micPausedRef.current = micPaused; }, [micPaused]);
  useEffect(() => { sendingRef.current = sending; }, [sending]);
  useEffect(() => { speakingRef.current = isSpeaking; }, [isSpeaking]);
  useEffect(() => { voiceActiveRef.current = voiceActive; }, [voiceActive]);

  // Scroll to bottom
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // ═══ SESSION LOADING ═══
  useEffect(() => {
    if (!urlSessionId || phase !== "loading") return;
    Promise.all([
      api.get(`/training/sessions/${urlSessionId}`),
      api.get("/akte/summary").catch(() => ({ data: { has_akte: false } })),
    ]).then(([sessionRes, akteRes]) => {
      setMessages(sessionRes.data.messages || []);
      setSessionId(urlSessionId);
      setLearningStage(sessionRes.data.learning_stage || null);
      if (akteRes.data.has_akte) setAkteInfo(akteRes.data);
      setPhase("mic_request");
    }).catch(() => navigate("/lernpfad", { replace: true }));
  }, [urlSessionId, phase, navigate]);

  // ═══ VOICE CYCLE: Speak new AI messages (plays TTS even in text mode) ═══
  useEffect(() => {
    if (!voiceEnabled) return; // Only needs TTS enabled, not mic active
    if (messages.length === 0) return;
    const lastIdx = messages.length - 1;
    const last = messages[lastIdx];
    if (last.role === "assistant" && lastIdx > lastSpokenIdxRef.current) {
      lastSpokenIdxRef.current = lastIdx;
      // Use speakRef to always get the latest speak function (prevents stale closure)
      setTimeout(() => speakRef.current(last.content), 400);
    }
  }, [messages, voiceEnabled, voiceActive]);

  // ═══ VOICE CYCLE: Track speaking state ═══
  useEffect(() => {
    if (isSpeaking) {
      setAiStatus("speaking");
      // STOP mic while AI speaks
      pauseMic();
    } else if (aiStatus === "speaking") {
      setAiStatus("idle");
      // THIS is the ONLY place that restarts the mic after AI finishes
      if (voiceActive && !micPausedRef.current) {
        setTimeout(() => {
          // Final check before starting — all via refs
          if (voiceActiveRef.current && !micPausedRef.current && !speakingRef.current && !sendingRef.current) {
            startMic();
          }
        }, 800);
      }
    }
  }, [isSpeaking]);

  // ═══ SAFETY: Reset status if TTS never starts (e.g., blocked by browser) ═══
  useEffect(() => {
    if (aiStatus === "thinking" && !sending) {
      // After sending completes, if voice should play but doesn't start within 5s, reset
      const timer = setTimeout(() => {
        if (aiStatus === "thinking" || (voiceEnabled && voiceActive && aiStatus !== "speaking" && aiStatus !== "idle")) {
          setAiStatus("idle");
        }
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [aiStatus, sending]);

  // ═══ AUTO-SEND after 4s silence ═══
  useEffect(() => {
    if (!liveTranscript || !isListening) return;
    clearTimeout(sendTimeoutRef.current);
    sendTimeoutRef.current = setTimeout(() => {
      const text = liveTranscript.trim();
      if (text.length > 2) {
        setLiveTranscript("");
        doSend(text);
      }
    }, 4000);
    return () => clearTimeout(sendTimeoutRef.current);
  }, [liveTranscript, isListening]);

  // ═══ MIC CONTROL — iOS-hardened, with error counting + fallback ═══
  const sttErrorCountRef = useRef(0);
  const sttFallbackRef = useRef(false);

  const startMic = useCallback(() => {
    if (speakingRef.current || sendingRef.current || micPausedRef.current || sttFallbackRef.current) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      // No STT support — immediate fallback
      sttFallbackRef.current = true;
      setVoiceEnabled(false);
      toast.error("Spracheingabe wird auf diesem Gerät nicht unterstützt. Sie können per Text antworten.");
      return;
    }

    // Kill any existing recognition
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch {}
      recognitionRef.current = null;
    }

    const r = new SR();
    r.lang = "de-DE";
    r.continuous = true;
    r.interimResults = true;

    r.onresult = (e) => {
      sttErrorCountRef.current = 0; // Reset error count on success
      let final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript;
      }
      if (final) setLiveTranscript(prev => (prev + " " + final).trim());
    };

    // onend: Auto-restart on ALL platforms (iOS ignores continuous=true, others may timeout)
    r.onend = () => {
      setIsListening(false);
      if (voiceActiveRef.current && !micPausedRef.current && !speakingRef.current && !sendingRef.current && !sttFallbackRef.current) {
        setTimeout(() => {
          if (voiceActiveRef.current && !micPausedRef.current && !speakingRef.current && !sendingRef.current) {
            try { r.start(); setIsListening(true); } catch {}
          }
        }, 300);
      }
    };

    r.onerror = (e) => {
      if (e.error === "not-allowed") {
        toast.error("Mikrofon-Zugriff verweigert.");
        sttFallbackRef.current = true;
        setIsListening(false);
        return;
      }
      // Count consecutive errors
      sttErrorCountRef.current += 1;
      if (sttErrorCountRef.current >= 3) {
        // Fallback to text mode after 3 consecutive errors
        sttFallbackRef.current = true;
        setIsListening(false);
        setVoiceActive(false);
        toast("Spracheingabe nicht verfügbar. Sie können per Text antworten.", { duration: 5000 });
        return;
      }
      setIsListening(false);
      // Auto-retry on "aborted" or "network" errors (common on iOS)
      if (["aborted", "network", "no-speech"].includes(e.error)) {
        if (voiceActiveRef.current && !micPausedRef.current && !speakingRef.current) {
          setTimeout(() => {
            if (voiceActiveRef.current && !micPausedRef.current && !speakingRef.current && !sttFallbackRef.current) {
              try {
                r.start(); setIsListening(true);
                if (sttErrorCountRef.current === 1) toast("Spracheingabe neu gestartet.", { duration: 2000 });
              } catch {}
            }
          }, 500);
        }
      }
    };

    recognitionRef.current = r;
    try { r.start(); setIsListening(true); } catch {}
  }, []); // No dependencies — reads everything from refs

  const pauseMic = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch {}
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, []);

  const stopMicCompletely = useCallback(() => {
    setVoiceActive(false);
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch {} recognitionRef.current = null; }
    setIsListening(false);
  }, []);

  // ═══ SEND MESSAGE ═══
  const doSend = async (text) => {
    const content = text || inputText.trim();
    if (!content || sending || !sessionId) return;
    pauseMic(); // Pause mic, don't kill voice mode
    clearTimeout(sendTimeoutRef.current);
    setInputText(""); setLiveTranscript(""); setSending(true); setAiStatus("thinking");
    setMessages(prev => [...prev, { role: "user", content }]);
    try {
      const res = await api.post(`/training/sessions/${sessionId}/message`, { content });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.ai_message.content }]);
      // Track turn info and session end signal
      if (res.data.turn) setTurnInfo({ turn: res.data.turn, max_turns: res.data.max_turns || 12 });
      if (res.data.session_should_end) {
        setSessionShouldEnd(true);
        // Stop mic after AI's closing message
        stopMicCompletely();
      }
    } catch { toast.error("Verbindungsfehler. Bitte erneut versuchen."); }
    setSending(false);
    // Only set idle if voice is disabled — otherwise voice cycle manages status
    if (!voiceEnabled) setAiStatus("idle");
  };

  // ═══ MIC PERMISSION + START ═══
  const requestMicAndStart = async () => {
    try {
      try { const ctx = new (window.AudioContext || window.webkitAudioContext)(); const b = ctx.createBuffer(1,1,22050); const s = ctx.createBufferSource(); s.buffer=b; s.connect(ctx.destination); s.start(0); } catch {}
      await navigator.mediaDevices.getUserMedia({ audio: true });
      setVoiceActive(true);
      setPhase("active");
      // Voice cycle starts: useEffect will speak first message, then start mic
    } catch {
      toast.error("Mikrofon konnte nicht aktiviert werden.");
      setPhase("active");
      setVoiceEnabled(false);
    }
  };

  const skipMic = () => { setPhase("active"); setVoiceActive(false); /* voiceEnabled stays true — user can still hear AI */ };

  // ═══ TAB VISIBILITY: Re-check mic when returning to tab ═══
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && voiceActiveRef.current && !micPausedRef.current && !speakingRef.current && !sttFallbackRef.current) {
        // Tab came back — restart mic if it was active
        setTimeout(() => {
          if (voiceActiveRef.current && !micPausedRef.current && !speakingRef.current && !isListening) {
            startMic();
          }
        }, 500);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [isListening, startMic]);

  // ═══ END CONVERSATION ═══
  const endConversation = async () => {
    if (ending) return;
    setEnding(true);
    stopMicCompletely(); stopVoice();
    const userMsgs = messages.filter(m => m.role === "user").length;
    if (userMsgs >= 2 && sessionId) {
      try {
        const res = await api.post(`/learning/complete/${sessionId}`);
        navigate(`/analysis/${sessionId}`, { state: { learning: res.data } }); return;
      } catch { try { await api.post(`/training/sessions/${sessionId}/complete`); } catch { /* fallback complete also failed — navigate anyway */ } }
    }
    navigate("/lernpfad");
  };

  // Cleanup
  useEffect(() => () => { stopMicCompletely(); stopVoice(); }, []);

  // ═══ RENDER: LOADING ═══
  if (phase === "loading") return (
    <div className="flex flex-col items-center justify-center h-[60vh]">
      <img src={avatarInfo.image} alt="" className="w-20 h-20 rounded-full object-cover opacity-60 mb-3" />
      <Loader2 className="w-6 h-6 animate-spin text-primary" />
    </div>
  );

  // ═══ RENDER: IDLE ═══
  if (!sessionId) return (
    <div className="flex flex-col items-center justify-center h-[60vh]">
      <p className="font-body text-muted-foreground">Kein aktives Gespräch.</p>
      <Button className="mt-4 font-body" onClick={() => navigate("/lernpfad")}>Zum Lernpfad</Button>
    </div>
  );

  // ═══ RENDER: MIC PERMISSION ═══
  if (phase === "mic_request") return (
    <div className="flex flex-col items-center justify-center h-[calc(100vh-10rem)] md:h-[calc(100vh-8rem)] px-4" data-testid="mic-permission">
      <img src={avatarInfo.image} alt={avatarInfo.name} className="w-28 h-28 rounded-full object-cover shadow-md mb-5" />
      <p className="font-body text-base font-medium mb-1">{avatarInfo.name}</p>
      <p className="font-body text-sm text-muted-foreground mb-6">ist bereit für das Gespräch</p>
      <div className="max-w-sm w-full space-y-3">
        <Button className="w-full h-12 font-body text-base bg-primary text-primary-foreground hover:bg-primary/90 gap-2" onClick={requestMicAndStart} data-testid="allow-mic-btn">
          <Mic className="w-5 h-5" /> Mikrofon erlauben & starten
        </Button>
        <Button variant="ghost" className="w-full font-body text-sm text-muted-foreground" onClick={skipMic}>
          <Keyboard className="w-4 h-4 mr-2" /> Lieber per Text antworten
        </Button>
      </div>
    </div>
  );

  // ═══ RENDER: ACTIVE CONVERSATION ═══
  const statusText = aiStatus === "speaking" ? "spricht..." : aiStatus === "thinking" ? "denkt..." : isListening ? "hört zu..." : "online";
  const statusRing = aiStatus === "speaking" ? "ring-2 ring-primary" : isListening ? "ring-2 ring-success" : aiStatus === "thinking" ? "ring-2 ring-warning" : "";
  const dotColor = aiStatus === "speaking" ? "bg-primary" : isListening ? "bg-success" : aiStatus === "thinking" ? "bg-warning" : "bg-muted";

  return (
    <div className="flex flex-col h-[calc(100vh-10rem)] md:h-[calc(100vh-8rem)]" data-testid="conversation-active">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border/30">
        <div className="flex items-center gap-3">
          <div className="relative">
            <img src={avatarInfo.image} alt="" className={`w-10 h-10 rounded-full object-cover transition-all ${statusRing}`} />
            <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-background ${dotColor}`} />
          </div>
          <div>
            <p className="font-body text-sm font-medium">{avatarInfo.name}</p>
            <p className="font-body text-[11px] text-muted-foreground">{statusText}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => { setVoiceEnabled(!voiceEnabled); if (isSpeaking) stopVoice(); }}>
            {voiceEnabled ? <Volume2 className="w-4 h-4 text-primary" /> : <VolumeX className="w-4 h-4 text-muted-foreground" />}
          </Button>
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs font-body text-error" onClick={endConversation} disabled={ending} data-testid="end-btn">
            {ending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <PhoneOff className="w-3.5 h-3.5 mr-1" />} {ending ? "Wird beendet..." : "Beenden"}
          </Button>
        </div>
      </div>

      {/* Akte Context Banner */}
      {akteInfo && (
        <div className="px-4 py-1.5 bg-primary/5 border-b border-primary/10 flex items-center justify-center gap-2" data-testid="akte-context-banner">
          <FileText className="w-3 h-3 text-primary" />
          <p className="font-body text-[10px] text-primary">Basiert auf Ihrer Akte ({akteInfo.delikt_art}{akteInfo.bak_wert ? `, ${akteInfo.bak_wert} ‰` : ""})</p>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <div className="max-w-2xl mx-auto space-y-3">
          {messages.map((msg, i) => (
            <div key={i} className={`flex items-end gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              {msg.role === "assistant" && <img src={avatarInfo.image} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mb-0.5" />}
              <div className={`max-w-[80%] px-3.5 py-2.5 ${msg.role === "user" ? "chat-bubble-user" : "chat-bubble-ai"}`}>
                <p className="font-body text-sm leading-relaxed">{msg.content}</p>
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex items-end gap-2">
              <img src={avatarInfo.image} alt="" className="w-7 h-7 rounded-full object-cover" />
              <div className="chat-bubble-ai px-4 py-3 flex gap-1.5"><div className="typing-dot" /><div className="typing-dot" /><div className="typing-dot" /></div>
            </div>
          )}
          {liveTranscript && (
            <div className="flex justify-end">
              <div className="chat-bubble-user px-3.5 py-2.5 opacity-50">
                <p className="font-body text-sm italic">{liveTranscript}...</p>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="border-t border-border/30 px-3 py-2 bg-background">
        {/* Session End Banner */}
        {sessionShouldEnd && !ending && (
          <div className="max-w-2xl mx-auto mb-2 bg-primary/5 border border-primary/20 rounded-xl p-3 flex items-center justify-between gap-3" data-testid="session-end-banner">
            <p className="font-body text-sm text-primary font-medium">Session abgeschlossen — Ihre Analyse wird erstellt.</p>
            <Button size="sm" className="font-body text-xs bg-primary text-primary-foreground gap-1 whitespace-nowrap" onClick={endConversation} disabled={ending} data-testid="view-analysis-btn">
              {ending ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              Analyse anzeigen
            </Button>
          </div>
        )}
        {/* Turn Progress (subtle) */}
        {turnInfo.turn > 0 && !sessionShouldEnd && (
          <div className="max-w-2xl mx-auto mb-1.5 flex justify-center">
            <p className="font-body text-[10px] text-muted-foreground/50">{turnInfo.turn} / {turnInfo.max_turns}</p>
          </div>
        )}
        {isListening && (
          <div className="max-w-2xl mx-auto mb-2 flex items-center justify-center gap-2">
            <div className="flex gap-1"><div className="typing-dot bg-success" /><div className="typing-dot bg-success" /><div className="typing-dot bg-success" /></div>
            <p className="font-body text-xs text-success">Ich höre zu...</p>
          </div>
        )}
        {micPaused && !isListening && voiceActive && (
          <div className="max-w-2xl mx-auto mb-2 flex items-center justify-center gap-2">
            <MicOff className="w-3.5 h-3.5 text-warning" />
            <p className="font-body text-xs text-warning">Mikrofon pausiert — tippen Sie oder klicken Sie das Mikro-Symbol</p>
          </div>
        )}
        <div className="max-w-2xl mx-auto flex items-center gap-2">
          <Button variant={isListening ? "default" : "outline"} size="icon"
            className={`rounded-full h-10 w-10 flex-shrink-0 ${isListening ? "bg-success hover:bg-success/90" : micPaused ? "bg-warning/20 border-warning/50" : ""}`}
            onClick={() => {
              if (isListening) {
                // User wants to mute mic — set ref FIRST (before stop triggers onend)
                micPausedRef.current = true;
                setMicPaused(true);
                pauseMic();
              } else {
                // User wants to unmute mic — clear ref FIRST
                micPausedRef.current = false;
                setMicPaused(false);
                setVoiceActive(true);
                startMic();
              }
            }} data-testid="mic-btn">
            {isListening ? <Mic className="w-4 h-4 text-white animate-pulse" /> : <MicOff className="w-4 h-4" />}
          </Button>
          <Input value={inputText} onChange={e => setInputText(e.target.value.slice(0, 2000))}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); doSend(); } }}
            placeholder={sessionShouldEnd ? "Session beendet — klicken Sie auf 'Analyse anzeigen'" : isListening ? "Sprechen Sie..." : "Oder tippen Sie hier..."}
            className="h-10 font-body text-sm rounded-full px-4" disabled={sending || sessionShouldEnd} maxLength={2000} data-testid="text-input" />
          {inputText.trim() && (
            <Button size="icon" className="rounded-full h-10 w-10 flex-shrink-0 bg-primary text-primary-foreground"
              onClick={() => doSend()} disabled={sending} data-testid="send-btn">
              <Send className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
